import { ActorRefFrom } from 'xstate';
import { z } from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { Employment } from '../../../types';
import { addressSchemaOptional, facilitySchemaRequiredNoAddress, optionSchema } from '../../types';
import { facilityMachine } from '../facility/facility.machine';
import { Events } from './events';

const isStrictIsoDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
};

export const employmentSchema = z
  .object({
    id: z.number().optional(),
    employer: z.string().min(1, 'Required').max(64, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(64)),
    // [MCV-1104] Separate empty-vs-malformed so an untouched field says
    // "Required" rather than the misleading "Invalid date format".
    startDate: z
      .string()
      .min(1, { message: ERROR_MESSAGES.REQUIRED })
      .refine(isStrictIsoDate, { message: ERROR_MESSAGES.INVALID_DATE }),
    currentFlag: z.boolean(), // Is the user still working here?
    endDate: z
      .string()
      .optional()
      .nullish()
      .refine((val) => !val || isStrictIsoDate(val), {
        message: ERROR_MESSAGES.INVALID_DATE,
      }),
    title: z.string().max(64, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(64)),
    department: z.string().max(64, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(64)),
    apptType: optionSchema,
    // [MCV-1749] The Employment facility step renders no address inputs and the API ignores a linked
    // facility's address (employment/controllers.py persist_custom_facility), so validating it
    // blocked Next with no visible message.
    facility: facilitySchemaRequiredNoAddress.safeExtend({
      address: z.custom<z.infer<typeof addressSchemaOptional>>(),
    }),
    leaveReason: z.string().max(4092, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(4092)),
    privilegesHeldCheckbox: z.boolean(), // SA privs_held_flag
    rehireToggle: z.boolean(), // SA rehire_eligible_flag
    rehireEligibleComment: z.string().max(4096, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(4096)),
    validFlag: z.boolean().optional(),
    warnings: z.record(z.string(), z.string()).optional(),
  })
  .refine(
    (data) => {
      return data.currentFlag || (!data.currentFlag && data.endDate);
    },
    { message: 'End date is required if not current employment.', path: ['endDate'] }
  )
  .refine(
    (data) => {
      return !data.endDate || data.startDate < data.endDate;
    },
    { message: ERROR_MESSAGES.INVALID_START_END_DATES, path: ['startDate'] }
  );

export type EmploymentFormValues = z.infer<typeof employmentSchema>;
export type CreateEmploymentFormValues = Omit<EmploymentFormValues, 'id'>;

export type EmploymentSortOptions = 'employer' | 'start_date' | 'title' | 'end_date' | 'id';
export type EmploymentSort = {
  sortBy: EmploymentSortOptions;
  employer: number;
  start_date: number;
  title: number;
  end_date: number;
  id: number;
  page: number;
  pageSize: number;
};

export interface Context {
  loading: boolean;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  employments: EmploymentFormValues[];
  facilityRef: ActorRefFrom<typeof facilityMachine>;
  employmentForm: EmploymentFormValues;
  modal: {
    edit: boolean;
    create: boolean;
  };
  sort: EmploymentSort;
  idToDelete: number;
}

export interface EmploymentResponse {
  data: Employment;
}

export interface GetEmploymentsResponse {
  data: Employment[];
}

export type SetEmploymentFormEvent = {
  type: 'ON_SHOW_ALERT';
  payload: {
    show?: boolean;
    timeout?: number;
    text?: string;
    subText?: string;
    type?: 'info' | 'error' | 'success' | 'warning' | 'plain';
  };
};

export interface Scope {
  context: Context;
  event: Events;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState system is typed as AnyActorRef which is too restrictive
  system?: any;
}
